import i18n from '@i18next/i18n';
import Presentation from '@src/framework/docs-api/presentation';
import { getAIEntranceExtension } from '@src/js/common/ark-extensions/dc-components/docs-component-ai-entrance';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { AISidebarFeature } from '@tencent/ai-sdk-interface';
import AiReadingIcon from '@tencent/docs-design-resources/icon/desktop/svg/ai_creat_regular_24.svg';
import LayoutCheck from '@tencent/docs-design-resources/icon/desktop/svg/layout_check_outline_light_24.svg';
import AiWritingIcon from '@tencent/docs-design-resources/icon/mobile/svg/ai_reading_regular_24.svg';
import AiGeneatingIcon from '@tencent/docs-design-resources/icon/mobile/svg/ai_recreat_regular_24.svg';
import PaletteRegular from '@tencent/docs-design-resources/icon/mobile/svg/palette_regular_24.svg';
import MenuToTemplate from '@tencent/docs-design-resources/mobile/svg/menu_to_template.svg';
import ToolbarText from '@tencent/docs-design-resources/mobile/svg/toolbar_text.svg';
import React from 'react';
import { Font, Note, Template, Theme } from './components';
import { IMobileAiService } from './mobile-ai-service.interface';
import type { IMobileRatingService } from '../../../slide-rating-and-improve/mobile-slide-rating-service.interface';
import type { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';

export const showPanel = async (
    aiService: IMobileAiService,
    mobileRatingService?: IDynamicService<IMobileRatingService>,
) => {
    tdw({
        opername: TDWOperationType.DOC_PPT_AI,
        module: TDWModuleType.MOBILE_ENTRANCE,
        action: TDWActionType.CLICK,
    });

    const AIEntranceExtension = await getAIEntranceExtension();
    const { editable } = SlideDataServices.PrivilegeManager() ?? {};

    const menuOptions = [
        {
            name: i18n.t('AI 阅读'),
            icon: AiWritingIcon,
            description: i18n.t('快速总结/深度阅读/思维导图总结'),
            async event(withEntranceArgs: <T>(options: T) => T) {
                tdw({
                    opername: TDWOperationType.DOC_PPT_AI,
                    module: TDWModuleType.READ,
                    action: TDWActionType.CLICK,
                    ver5: TDWModuleType.MOBILE,
                });
                const aiController = await aiService.getSidebarController();

                // 只读用户，不显示《对话》
                aiController.show(withEntranceArgs({
                    featureList: editable ? [
                        AISidebarFeature.SUMMARY,
                        AISidebarFeature.DEEPREAD,
                        AISidebarFeature.MINDMAP,
                        AISidebarFeature.FILEQA,
                    ] : [
                        AISidebarFeature.SUMMARY,
                        AISidebarFeature.DEEPREAD,
                        AISidebarFeature.MINDMAP,
                    ],
                }));
            },
        },
        editable && {
            name: i18n.t('AI 写备注'),
            icon: AiReadingIcon,
            description: i18n.t('快速生成备注'),
            component: (
                <Note
                    onMount={() => {
                        tdw({
                            opername: TDWOperationType.DOC_PPT_AI,
                            module: TDWModuleType.WRITE_NOTE,
                            action: TDWActionType.CLICK,
                            ver5: TDWModuleType.MOBILE,
                        });
                    }}
                    onConfirm={(noteContent) => {
                        Presentation.getInstance().insertSlideNotes(noteContent);
                        AIEntranceExtension.hideAIMenu();
                    }}
                />
            ),
        },
        editable && {
            name: i18n.t('AI 美化'),
            icon: AiGeneatingIcon,
            description: i18n.t('美化PPT'),
            children: [
                {
                    name: i18n.t('排版检查'),
                    icon: LayoutCheck,
                    event: async () => {
                        tdw({
                            opername: TDWOperationType.DOC_PPT_AI,
                            module: TDWModuleType.LAYOUT_CHECK,
                            action: TDWActionType.CLICK,
                            ver5: TDWModuleType.MOBILE,
                        });

                        const service = await mobileRatingService?.promise();
                        if (!service) return;

                        service.showInPreview();
                        AIEntranceExtension.hideAIMenu();
                    },
                },
                {
                    type: 'group',
                    label: 'string',
                    children: [
                        {
                            name: i18n.t('字体切换'),
                            icon: ToolbarText,
                            component: (
                                <Font
                                    onMount={() => {
                                        tdw({
                                            opername: TDWOperationType.DOC_PPT_AI,
                                            module: TDWModuleType.FONT_SWITCH,
                                            action: TDWActionType.CLICK,
                                            ver5: TDWModuleType.MOBILE,
                                        });
                                    }}
                                    onConfirm={({ font, range }) => {
                                        Presentation.getInstance().applyFont(font, range as any);
                                        AIEntranceExtension.hideAIMenu();
                                    }}
                                />
                            ),
                        },
                        {
                            name: i18n.t('配色切换'),
                            icon: PaletteRegular,
                            component: (
                                <Theme
                                    onMount={() => {
                                        tdw({
                                            opername: TDWOperationType.DOC_PPT_AI,
                                            module: TDWModuleType.COLOR_SWITCH,
                                            action: TDWActionType.CLICK,
                                            ver5: TDWModuleType.MOBILE,
                                        });
                                    }}
                                    onConfirm={(themeId) => {
                                        Presentation.getInstance().applyThemeColor(themeId);
                                        AIEntranceExtension.hideAIMenu();
                                    }}
                                />
                            ),
                        },
                        {
                            name: i18n.t('模板切换'),
                            icon: MenuToTemplate,
                            component: (
                                <Template
                                    onMount={() => {
                                        tdw({
                                            opername: TDWOperationType.DOC_PPT_AI,
                                            module: TDWModuleType.TEMPLATE_SWITCH,
                                            action: TDWActionType.CLICK,
                                            ver5: TDWModuleType.MOBILE,
                                        });
                                    }}
                                    onConfirm={(templateId) => {
                                        Presentation.getInstance().applyTemplate(templateId, false);
                                        AIEntranceExtension.hideAIMenu();
                                    }}
                                />
                            ),
                        },
                    ],
                },
            ],
        },
    ];
    AIEntranceExtension.showAIMenu({
        menu: menuOptions.filter(Boolean),
    });
};
